import { fetchWithAuth } from "../utils/fetchWithAuth";
import briefManifest from "../shared/featureWorkflows/briefManifest";

class BriefWorkflowService {
  constructor() {
    this.WORKFLOW_STEPS = briefManifest.steps.map(step => ({
      ...step,
      status: "pending",
    }));

    this.workflowResults = {};
  }

  getSteps() {
    return [...this.WORKFLOW_STEPS];
  }

  resetWorkflow() {
    this.workflowResults = {};
    this.WORKFLOW_STEPS.forEach((step) => {
      step.status = "pending";
    });
  }

  async executeWorkflow(params, callbacks) {
    const { designChallenge } = params;
    const { onStepStart, onStepComplete, onComplete, onError } = callbacks;

    console.log("📋 BRIEF WORKFLOW: Starting", {
      designChallenge: designChallenge?.substring(0, 100) + "...",
    });

    try {
      const steps = this.getSteps();

      if (onStepStart) onStepStart(0, { ...steps[0], agent: "Product Lead" });

      const briefResults = await this.executeDeepAnalysis(designChallenge);
      console.log("📝 Brief Analysis & Synthesis completed by Design Lead");
      if (onStepComplete)
        onStepComplete(
          0,
          { ...steps[0], status: "completed" },
          briefResults,
        );

      const finalReport = {
        ...briefResults,
        metadata: {
          generatedAt: new Date().toISOString(),
          workflowType: "brief",
        },
      };

      console.log("🔥🔥🔥 BRIEF WORKFLOW: About to call onComplete callback");
      if (onComplete) {
        console.log("🔥🔥🔥 BRIEF WORKFLOW: onComplete exists, calling it now");
        onComplete(finalReport);
        console.log("🔥🔥🔥 BRIEF WORKFLOW: onComplete callback completed");
      } else {
        console.log("🔥🔥🔥 BRIEF WORKFLOW: onComplete is MISSING!");
      }
      console.log("🔥🔥🔥 BRIEF WORKFLOW: About to return finalReport");
      return finalReport;
    } catch (error) {
      console.error("❌ Brief workflow failed:", error);
      if (onError) onError(error);
      throw error;
    }
  }

  async executeDeepAnalysis(designChallenge) {
    try {
      const result = await fetchWithAuth("/autonomous-ai/autonomous-analysis", {
        method: "POST",
        body: {
          design_challenge: designChallenge,
          analysis_type: "deep_contextual",
          use_csv_as_knowledge: false,
          agentId: "DESIGN_LEAD",
          context: { source: "brief_workflow", step: "deep_analysis" },
        },
      });

      if (!result.ok) {
        throw new Error(`Deep analysis failed with status ${result.status}`);
      }

      const analysisData = await result.json();

      // Store raw analysis results
      this.workflowResults.deepAnalysis = {
        ...analysisData,
        contextualFactors: analysisData.contextual_factors || [],
        domainSpecificInsights: analysisData.domain_insights || [],
        uniqueConstraints: analysisData.unique_constraints || [],
        opportunityAreas: analysisData.opportunity_areas || [],
      };

      // Synthesize and normalize the data (previously separate step)
      const synthesizedResults = this.synthesizeBriefData(this.workflowResults.deepAnalysis);
      
      // Return both synthesized results AND raw deepAnalysis for downstream consumers
      return {
        ...synthesizedResults,
        deepAnalysis: this.workflowResults.deepAnalysis,
      };
    } catch (error) {
      console.error("Deep analysis step failed:", error);
      throw error;
    }
  }

  // Data normalization and validation (previously executeBriefSynthesis)
  synthesizeBriefData(deepAnalysis) {
    console.log("🎯 BRIEF SYNTHESIS: Normalizing data...");
    console.log("🎯 BRIEF SYNTHESIS: Deep analysis data received:", {
      hasShortSummary: !!deepAnalysis.short_summary,
      hasProblemStatement: !!deepAnalysis.problem_statement,
      hasGoalStatement: !!deepAnalysis.goal_statement,
      hasTargetStatement: !!deepAnalysis.target_statement,
      hasObjectives: Array.isArray(deepAnalysis.objectives_statement),
      objectivesCount: deepAnalysis.objectives_statement?.length || 0,
      hasStakeholders: Array.isArray(deepAnalysis.stakeholders_involvement),
      stakeholdersCount: deepAnalysis.stakeholders_involvement?.length || 0,
    });

    const results = {
      planName: deepAnalysis.plan_name,
      shortSummary: deepAnalysis.short_summary,
      problemStatement: deepAnalysis.problem_statement,
      goalStatement: deepAnalysis.goal_statement,
      targetStatement: deepAnalysis.target_statement,
      contextualFactors:
        deepAnalysis.contextualFactors ||
        deepAnalysis.contextual_factors ||
        [],
      domainInsights:
        deepAnalysis.domainSpecificInsights ||
        deepAnalysis.domain_insights ||
        [],
      uniqueConstraints:
        deepAnalysis.uniqueConstraints ||
        deepAnalysis.unique_constraints ||
        [],

      objectives: Array.isArray(deepAnalysis.objectives_statement)
        ? deepAnalysis.objectives_statement
        : deepAnalysis.opportunity_areas || [],

      stakeholders: Array.isArray(deepAnalysis.stakeholders_involvement)
        ? deepAnalysis.stakeholders_involvement
        : [],

      isBrief: true,
      workflowType: "brief",
      createdBy: "Brief Workflow Service",
    };

    console.log("🎯 BRIEF SYNTHESIS: Compiled results:", {
      hasShortSummary: !!results.shortSummary,
      hasProblemStatement: !!results.problemStatement,
      hasGoalStatement: !!results.goalStatement,
      hasTargetStatement: !!results.targetStatement,
      objectivesCount: results.objectives?.length || 0,
      stakeholdersCount: results.stakeholders?.length || 0,
    });

    if (!results.objectives || results.objectives.length === 0) {
      console.error(
        "❌ BRIEF SYNTHESIS: No objectives found in deep analysis",
      );
      throw new Error(
        "AI failed to generate objectives. Please try again with a clearer design challenge.",
      );
    }

    if (!results.goalStatement) {
      console.error("❌ BRIEF SYNTHESIS: No goal statement found");
      throw new Error(
        "AI failed to generate goal statement. Please try again.",
      );
    }

    this.workflowResults.synthesis = {
      complete: true,
      timestamp: new Date().toISOString(),
      briefData: results,
    };

    console.log("✅ BRIEF SYNTHESIS: Completed successfully");
    return results;
  }
}

export default BriefWorkflowService;
